import { useState } from 'react';
import { Activity, TrendingUp, TrendingDown, AlertCircle, RefreshCw, Database, Zap } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';

/**
 * KPIsPage - Real-time Key Performance Indicators for risk monitoring
 */

interface KPI {
  id: string;
  name: string;
  category: string;
  value: number;
  unit: string;
  threshold: number;
  trend: 'up' | 'down' | 'stable';
  status: 'healthy' | 'warning' | 'critical';
  source: string;
  lastUpdated: Date;
}

const mockKPIs: KPI[] = [
  {
    id: '1',
    name: 'System Uptime',
    category: 'Operational',
    value: 99.97,
    unit: '%',
    threshold: 99.5,
    trend: 'stable',
    status: 'healthy',
    source: 'AWS CloudWatch',
    lastUpdated: new Date(Date.now() - 120000),
  },
  {
    id: '2',
    name: 'Failed Login Attempts',
    category: 'Security',
    value: 47,
    unit: 'attempts/hour',
    threshold: 50,
    trend: 'down',
    status: 'healthy',
    source: 'Auth0',
    lastUpdated: new Date(Date.now() - 60000),
  },
  {
    id: '3',
    name: 'API Response Time',
    category: 'Performance',
    value: 245,
    unit: 'ms',
    threshold: 300,
    trend: 'up',
    status: 'warning',
    source: 'Datadog',
    lastUpdated: new Date(Date.now() - 30000),
  },
  {
    id: '4',
    name: 'Database Connection Pool',
    category: 'Infrastructure',
    value: 87,
    unit: '%',
    threshold: 80,
    trend: 'up',
    status: 'critical',
    source: 'RDS Metrics',
    lastUpdated: new Date(Date.now() - 45000),
  },
  {
    id: '5',
    name: 'Compliance Score',
    category: 'Compliance',
    value: 94,
    unit: '%',
    threshold: 90,
    trend: 'up',
    status: 'healthy',
    source: 'Internal Audit',
    lastUpdated: new Date(Date.now() - 300000),
  },
  {
    id: '6',
    name: 'Data Backup Success Rate',
    category: 'Business Continuity',
    value: 100,
    unit: '%',
    threshold: 99,
    trend: 'stable',
    status: 'healthy',
    source: 'Veeam',
    lastUpdated: new Date(Date.now() - 180000),
  },
  {
    id: '7',
    name: 'Unpatched Vulnerabilities',
    category: 'Security',
    value: 12,
    unit: 'CVEs',
    threshold: 10,
    trend: 'down',
    status: 'warning',
    source: 'Qualys',
    lastUpdated: new Date(Date.now() - 90000),
  },
  {
    id: '8',
    name: 'Transaction Error Rate',
    category: 'Operational',
    value: 0.08,
    unit: '%',
    threshold: 0.5,
    trend: 'stable',
    status: 'healthy',
    source: 'Application Logs',
    lastUpdated: new Date(Date.now() - 15000),
  },
  {
    id: '9',
    name: 'Third-Party Risk Score',
    category: 'Third-Party',
    value: 76,
    unit: '/100',
    threshold: 70,
    trend: 'down',
    status: 'warning',
    source: 'BitSight',
    lastUpdated: new Date(Date.now() - 600000),
  },
  {
    id: '10',
    name: 'Incident Response Time',
    category: 'Security',
    value: 18,
    unit: 'minutes',
    threshold: 30,
    trend: 'down',
    status: 'healthy',
    source: 'PagerDuty',
    lastUpdated: new Date(Date.now() - 240000),
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'healthy':
      return 'text-success-500 bg-success-500/20 border-success-500/30';
    case 'warning':
      return 'text-warning-500 bg-warning-500/20 border-warning-500/30';
    case 'critical':
      return 'text-danger-500 bg-danger-500/20 border-danger-500/30';
    default:
      return 'text-neutral-500 bg-neutral-500/20 border-neutral-500/30';
  }
};

const getTrendIcon = (trend: string, status: string) => {
  if (trend === 'up') {
    return <TrendingUp className={`w-4 h-4 ${status === 'critical' ? 'text-danger-500' : 'text-success-500'}`} />;
  } else if (trend === 'down') {
    return <TrendingDown className={`w-4 h-4 ${status === 'healthy' ? 'text-success-500' : 'text-warning-500'}`} />;
  }
  return <Activity className="w-4 h-4 text-neutral-500" />;
};

const formatTimeAgo = (date: Date) => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

export function KPIsPage() {
  const [kpis] = useState<KPI[]>(mockKPIs);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(kpis.map(k => k.category)))];
  const filteredKPIs = selectedCategory === 'All' 
    ? kpis 
    : kpis.filter(k => k.category === selectedCategory);

  const healthyCount = kpis.filter(k => k.status === 'healthy').length;
  const warningCount = kpis.filter(k => k.status === 'warning').length;
  const criticalCount = kpis.filter(k => k.status === 'critical').length;
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="kpis"
        icon={Activity}
        title="Real-time KPIs"
        description="Monitor key performance indicators and risk metrics from integrated systems in real-time"
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Real-time KPIs</h1>
          <p className="text-neutral-400 mt-2">
            Monitor key performance indicators and risk metrics from integrated systems in real-time
          </p>
        </div>
        <button className="btn-outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh All
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Activity className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Active Metrics</p>
              <p className="text-2xl font-bold text-white">{kpis.length}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Real-time monitoring</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Healthy</p>
              <p className="text-2xl font-bold text-white">{healthyCount}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Within thresholds</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-warning-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Warning</p>
              <p className="text-2xl font-bold text-white">{warningCount}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Approaching limits</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-danger-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-danger-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Critical</p>
              <p className="text-2xl font-bold text-white">{criticalCount}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Immediate attention</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-neutral-400">Filter by category:</span>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === category
                ? 'bg-primary-500 text-black'
                : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredKPIs.map((kpi) => (
          <div key={kpi.id} className="card">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white">{kpi.name}</h3>
                    {getTrendIcon(kpi.trend, kpi.status)}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <Database className="w-3 h-3" />
                    <span>{kpi.source}</span>
                    <span>•</span>
                    <span>{formatTimeAgo(kpi.lastUpdated)}</span>
                  </div>
                </div>
                <span className={`badge ${getStatusColor(kpi.status)}`}>
                  {kpi.status}
                </span>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white">{kpi.value}</span>
                  <span className="text-sm text-neutral-400">{kpi.unit}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-neutral-500">
                  <Zap className="w-3 h-3" />
                  <span>Threshold: {kpi.threshold} {kpi.unit}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-neutral-400">
                  <span>Performance</span>
                  <span>{Math.round((kpi.value / kpi.threshold) * 100)}%</span>
                </div>
                <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      kpi.status === 'critical'
                        ? 'bg-danger-500'
                        : kpi.status === 'warning'
                        ? 'bg-warning-500'
                        : 'bg-success-500'
                    }`}
                    style={{ width: `${Math.min((kpi.value / kpi.threshold) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
